import React from 'react';
import { Home, Database, Table2, ArrowLeftRight, History, MessageSquare, InfoIcon, ChartColumn } from 'lucide-react';
import { DatasetsSidePanel } from '@/components/pages/DatasetsSidePanel';
import { WorkspaceSwitcherCompact } from '@/components/navigation/workspace-switcher';

export interface StandardNavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  href: string;
  type: 'standard';
  panel?: React.ReactNode;
  panelTitle?: string;
  panelWidth?: string;
}

export interface ContextualNavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  href?: string;
  type: 'contextual';
  panel?: React.ReactNode;
  panelTitle?: string;
  panelWidth?: string;
  badgeCount?: number;
  badgeVariant?: 'default' | 'secondary' | 'destructive';
  disabled?: boolean;
  onClick?: () => void;
}

export interface FooterNavItem {
  id: string;
  label: string;
  icon?: React.ReactNode;
  component?: React.ReactNode;
  href?: string;
  onClick?: () => void;
  target?: string;
  rel?: string;
}

export type NavItem = StandardNavItem | ContextualNavItem | FooterNavItem;

export interface NavigationConfig {
  header: React.ReactNode;
  primary: StandardNavItem[];
  contextual: ContextualNavItem[];
  footer: FooterNavItem[];
}

// Standard navigation items that appear on every page
export const getStandardNavItems = (): StandardNavItem[] => [
  {
    id: 'home',
    label: 'Home',
    icon: <Home className="h-5 w-5" />,
    href: '/home',
    type: 'standard'
  },
  {
    id: 'datasets',
    label: 'Datasets',
    icon: <Database className="h-5 w-5" />,
    href: '/datasets',
    type: 'standard'
  }
];

// Get contextual nav items for dataset pages
export const getDatasetContextualItems = ({
  baseUrl,
  viewUrl,
  chartsUrl,
  tableCount = 0,
  chartCount = 0,
  isLoading = false,
  tablesPanel,
  chartsPanel
}: {
  baseUrl: string;
  viewUrl: string;
  chartsUrl: string;
  tableCount?: number;
  chartCount?: number;
  isLoading?: boolean;
  tablesPanel?: React.ReactNode;
  chartsPanel?: React.ReactNode;
}): ContextualNavItem[] => [
  {
    id: 'about',
    label: 'About',
    icon: <InfoIcon className="h-5 w-5" />,
    href: baseUrl,
    type: 'contextual',
    disabled: isLoading
  },
  {
    id: 'tables',
    label: isLoading ? 'Loading...' : 'Tables',
    icon: <Table2 className="h-5 w-5" />,
    href: viewUrl,
    type: 'contextual',
    disabled: isLoading,
    badgeCount: tableCount,
    badgeVariant: 'secondary',
    panel: tablesPanel,
    panelTitle: 'Tables'
  },
  {
    id: 'charts',
    label: isLoading ? 'Loading...' : 'Charts',
    icon: <ChartColumn className="h-5 w-5" />,
    href: chartsUrl,
    type: 'contextual',
    disabled: isLoading,
    badgeCount: chartCount,
    badgeVariant: 'secondary',
    panel: chartsPanel,
    panelTitle: 'Charts'
  }
];

// Get footer navigation items
export const getFooterNavItems = ({
  onChangelogClick,
  onThemeToggle,
  userDropdown
}: {
  onChangelogClick: () => void;
  onThemeToggle: () => void;
  userDropdown: React.ReactNode;
}): FooterNavItem[] => [
  {
    id: 'view',
    label: 'View',
    icon: <Table2 className="h-5 w-5" />,
    href: '/app'
  },
  {
    id: 'convert',
    label: 'Convert',
    icon: <ArrowLeftRight className="h-5 w-5" />,
    href: '/convert'
  },
  {
    id: 'changelog',
    label: "What's New",
    icon: <History className="h-5 w-5" />,
    onClick: onChangelogClick
  },
  {
    id: 'feedback',
    label: 'Feedback',
    icon: <MessageSquare className="h-5 w-5" />,
    href: 'https://tablab.featurebase.app',
    target: '_blank',
    rel: 'noopener noreferrer'
  },
  {
    id: 'theme',
    label: 'Theme',
    onClick: onThemeToggle
  },
  {
    id: 'user',
    label: 'User',
    component: userDropdown
  }
];

// Get standard navigation config for app pages
export const getAppNavigationConfig = ({
  onChangelogClick,
  onThemeToggle,
  userDropdown,
  themeIcon
}: {
  onChangelogClick: () => void;
  onThemeToggle: () => void;
  userDropdown: React.ReactNode;
  themeIcon: React.ReactNode;
}): NavigationConfig => {
  const footerItems = getFooterNavItems({ onChangelogClick, onThemeToggle, userDropdown });
  
  // Add theme icon to theme item
  const footerWithTheme = footerItems.map(item => 
    item.id === 'theme' 
      ? { ...item, icon: themeIcon }
      : item
  );

  // Add sidebar panel to datasets nav item
  const primaryWithPanels = getStandardNavItems().map(item => 
    item.id === 'datasets' 
      ? { ...item, panel: <DatasetsSidePanel />, panelTitle: 'Datasets' }
      : item
  );

  return {
    header: <WorkspaceSwitcherCompact />,
    primary: primaryWithPanels,
    contextual: [], // Remove datasets from contextual
    footer: footerWithTheme
  };
};

// Get navigation config for dataset pages
export const getDatasetNavigationConfig = ({
  baseUrl,
  viewUrl,
  chartsUrl,
  tableCount,
  chartCount,
  isLoading,
  tablesPanel,
  chartsPanel,
  onChangelogClick,
  onThemeToggle,
  userDropdown,
  themeIcon
}: {
  baseUrl: string;
  viewUrl: string;
  chartsUrl: string;
  tableCount: number;
  chartCount: number;
  isLoading: boolean;
  tablesPanel: React.ReactNode;
  chartsPanel: React.ReactNode;
  onChangelogClick: () => void;
  onThemeToggle: () => void;
  userDropdown: React.ReactNode;
  themeIcon: React.ReactNode;
}): NavigationConfig => {
  const footerItems = getFooterNavItems({ onChangelogClick, onThemeToggle, userDropdown });
  
  // Add theme icon to theme item
  const footerWithTheme = footerItems.map(item => 
    item.id === 'theme' 
      ? { ...item, icon: themeIcon }
      : item
  );

  // Add sidebar panel to datasets nav item (same as app navigation)
  const primaryWithPanels = getStandardNavItems().map(item => 
    item.id === 'datasets' 
      ? { ...item, panel: <DatasetsSidePanel />, panelTitle: 'Datasets' }
      : item
  );

  return {
    header: <WorkspaceSwitcherCompact />,
    primary: primaryWithPanels,
    contextual: [
      // Only dataset-specific contextual items (no duplicate datasets)
      ...getDatasetContextualItems({
        baseUrl,
        viewUrl,
        chartsUrl,
        tableCount,
        chartCount,
        isLoading,
        tablesPanel,
        chartsPanel
      })
    ],
    footer: footerWithTheme
  };
}; 